import * as React from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /** @deprecated Prefer panelClassName for the inner panel sizing */
  className?: string;
  /** Applies to Dialog.Panel — use e.g. max-w-5xl for split layouts */
  panelClassName?: string;
}

export function DialogRoot({
  open,
  onOpenChange,
  children,
  className,
  panelClassName,
}: DialogProps) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={() => onOpenChange(false)} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          {/* THE app scrim recipe: black/50, z-50 on the dialog root. Every
              overlay in the app should ride this component (one scrim, one
              z-index — no bespoke fixed inset-0 layers, no z-index wars). */}
          <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel
              className={cn(
                "w-full max-w-md bg-card rounded-lg shadow-xl overflow-hidden",
                panelClassName,
                className,
              )}
            >
              {children}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

interface DialogContentProps {
  children: React.ReactNode;
  className?: string;
}

export function DialogContent({ children, className }: DialogContentProps) {
  return <div className={cn("p-6", className)}>{children}</div>;
}

interface DialogHeaderProps {
  children: React.ReactNode;
  className?: string;
  showCloseButton?: boolean;
  onClose?: () => void;
}

export function DialogHeader({
  children,
  className,
  showCloseButton = false,
  onClose,
}: DialogHeaderProps) {
  return (
    <div className={cn("flex items-center justify-between mb-4", className)}>
      <Dialog.Title className="text-lg font-medium text-foreground">
        {children}
      </Dialog.Title>
      {showCloseButton && onClose && (
        <button
          onClick={onClose}
          aria-label="Close"
          className="text-muted-foreground hover:text-muted-foreground transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

interface DialogFooterProps {
  children: React.ReactNode;
  className?: string;
}

export function DialogFooter({ children, className }: DialogFooterProps) {
  return (
    <div className={cn("flex justify-end gap-3 mt-6", className)}>
      {children}
    </div>
  );
}

export { DialogRoot as Dialog };
